import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Download, Search, ChevronRight, ChevronLeft, Users, UserCheck, UserX, TrendingUp, Target, UserPlus, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/cn';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { OrgStatTile } from '@/features/OrgLayout/primitives/OrgStatTile';
import { ReadinessBadge } from '@/features/OrgLayout/ReadinessBadge';
import { useOrgBatchManagement } from '@/lib/hooks/useOrg';
import { AddStudentsDialog } from '@/features/OrgLayout/management/AddStudentsDialog';
import type { OrgBatchDetail, OrgStudentRow } from '@/lib/api/org';

const PAGE_SIZE = 10;
const SELECT_TRIGGER = 'h-[42px] w-auto min-w-[140px] rounded-[14px] border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280]';

function isActive(s: OrgStudentRow) {
  return s.enrollment_status === 'active';
}

function exportCsv(rows: OrgStudentRow[], batchName: string) {
  const header = ['Name', 'Email', 'Completion %', 'Attendance %', 'Avg score', 'Status', 'Topics'];
  const body = rows.map((s) => [
    s.name,
    s.email,
    Math.round(s.completion_pct),
    Math.round(s.attendance_pct),
    Math.round(s.avg_score),
    s.enrollment_status,
    `${s.completed_topics}/${s.total_topics}`,
  ]);
  downloadCsv(csvFilename(batchName, 'students'), header, body);
}

export function BatchStudentsTab({ batchId, data }: { batchId: string; data: OrgBatchDetail }) {
  const navigate = useNavigate();
  const { batch, students } = data;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [removingStudent, setRemovingStudent] = useState<{ userId: string; name: string } | null>(null);
  const mgmt = useOrgBatchManagement(batchId);

  const removeStudent = (userId: string, name: string) => {
    setRemovingStudent({ userId, name });
  };

  const confirmRemoveStudent = () => {
    if (!removingStudent) return;
    mgmt.removeStudent.mutate({ batchId, userId: removingStudent.userId });
    setRemovingStudent(null);
  };

  const readyCount = students.filter((s) => s.readiness_band === 'product_ready' || s.readiness_band === 'service_ready').length;
  const readyPct = students.length ? Math.round((readyCount / students.length) * 100) : 0;
  const activeCount = students.filter(isActive).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      if (q && !s.name.toLowerCase().includes(q) && !s.email.toLowerCase().includes(q)) return false;
      if (status === 'active' && !isActive(s)) return false;
      if (status === 'inactive' && isActive(s)) return false;
      return true;
    });
  }, [students, search, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetPage = () => setPage(1);
  const goToStudent = (userId: string) =>
    navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId, userId } });

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#111827]">Students</h2>
          <p className="text-sm text-[#6b7280]">View and manage students in this batch.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportCsv(filtered, batch.name)}
            className="inline-flex items-center gap-2 rounded-[14px] border border-[#ececec] bg-white px-4 py-2.5 text-sm font-medium text-[#111827]"
          >
            <Download className="h-4 w-4" /> Export
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-[14px] bg-[#ff5a1f] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#e64f18]"
          >
            <UserPlus className="h-4 w-4" /> Add students
          </button>
        </div>
      </div>
      <AddStudentsDialog batchId={batchId} open={addOpen} onOpenChange={setAddOpen} />

      {/* Stat tiles */}
      <OrgCard className="flex overflow-hidden">
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={Users} iconBg="#fff4f0" iconColor="#ff5a1f" value={students.length} label="Total Students" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={UserCheck} iconBg="#f0fdf4" iconColor="#16a34a" value={activeCount} label="Active Students" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={UserX} iconBg="#fef2f2" iconColor="#dc2626" value={students.length - activeCount} label="Inactive Students" /></div>
        <div className="flex-1 border-r border-[#ececec]"><OrgStatTile icon={TrendingUp} iconBg="#eff6ff" iconColor="#2563eb" value={`${Math.round(batch.avg_completion_pct)}%`} label="Avg Progress" /></div>
        <div className="flex-1"><OrgStatTile icon={Target} iconBg="#f0fdfa" iconColor="#0f766e" value={`${readyPct}%`} label="Placement Ready" /></div>
      </OrgCard>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex h-[42px] w-[320px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
          <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            placeholder="Search students..."
            className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); resetPage(); }}>
          <SelectTrigger className={SELECT_TRIGGER}><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <OrgCard className="overflow-hidden">
        {pageItems.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9ca3af]">No students match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-[#ececec]">
                  {['Student', 'Progress', 'Attendance', 'Status', 'Topics', ''].map((h, i) => (
                    <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s, i) => (
                  <tr
                    key={s.user_id}
                    onClick={() => goToStudent(s.user_id)}
                    className={cn('cursor-pointer hover:bg-[#fafafa]', i !== pageItems.length - 1 && 'border-b border-[#ececec]')}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <OrgAvatar name={s.name} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                          <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <OrgProgressBar pct={s.completion_pct} className="w-[80px]" barClassName="bg-[#0f766e]" />
                        <span className="text-sm tabular-nums text-[#111827]">{Math.round(s.completion_pct)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums text-[#374151]">{Math.round(s.attendance_pct)}%</td>
                    <td className="px-6 py-4"><ReadinessBadge band={s.readiness_band} /></td>
                    <td className="px-6 py-4 text-sm tabular-nums text-[#374151]">{s.completed_topics}/{s.total_topics}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          aria-label={`Remove ${s.name}`}
                          onClick={(e) => { e.stopPropagation(); removeStudent(s.user_id, s.name); }}
                          className="rounded-lg p-1.5 text-[#9ca3af] hover:bg-red-50 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <ChevronRight className="h-4 w-4 text-[#9ca3af]" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[#ececec] px-6 py-4">
          <p className="text-sm text-[#9ca3af]">
            {filtered.length === 0 ? 'Showing 0 students' : `Showing ${from} to ${to} of ${filtered.length} students`}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous page" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40">
              <ChevronLeft className="h-[14px] w-[14px]" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              return (
                <button key={n} type="button" onClick={() => setPage(n)} className={cn('flex h-[34px] w-[34px] items-center justify-center rounded-[14px] text-sm font-semibold', n === currentPage ? 'bg-[#111827] text-white' : 'border border-[#ececec] text-[#111827]')}>
                  {n}
                </button>
              );
            })}
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:opacity-40">
              <ChevronRight className="h-[14px] w-[14px]" />
            </button>
          </div>
        </div>
      </OrgCard>

      <AlertDialog open={!!removingStudent} onOpenChange={(open) => !open && setRemovingStudent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removingStudent?.name} from this batch?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveStudent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
